import { useState, useRef, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useProfile } from "@/hooks/useProfile";
import { compressImage, validateVideo, validateFileSize, isProRestrictedFile } from "@/utils/mediaUtils";
import { Progress } from "@/components/ui/progress";
import { PostInput } from "./components/PostInput";
import { MediaPreview } from "./components/MediaPreview";
import { FileList } from "./components/FileList";
import { PostActions } from "./components/PostActions";

const MAX_FILE_SIZE = 10; // 10MB

export const CreatePost = () => {
  const [prompt, setPrompt] = useState("");
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [mentionSearch, setMentionSearch] = useState("");
  const [showMentions, setShowMentions] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [isPrivate, setIsPrivate] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const mediaInputRef = useRef<HTMLInputElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { user } = useAuth();
  const { profile, isLoading: isProfileLoading } = useProfile();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const maxLength = 500; // Changed from 250 to 500

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setPrompt(value.slice(0, maxLength));
    
    const cursorPos = e.target.selectionStart || 0;
    setCursorPosition(cursorPos);

    const lastAtSymbol = value.lastIndexOf('@', cursorPos);
    if (lastAtSymbol !== -1 && lastAtSymbol < cursorPos) {
      const textAfterAt = value.slice(lastAtSymbol + 1, cursorPos);
      if (!textAfterAt.includes(' ')) {
        setMentionSearch(textAfterAt);
        setShowMentions(true);
        return;
      }
    }
    setShowMentions(false);
  };

  const handleSelectMention = (username: string) => {
    if (!textareaRef.current) return;

    const lastAtSymbol = prompt.lastIndexOf('@', cursorPosition);
    const newPrompt = prompt.slice(0, lastAtSymbol) + 
      `@${username} ` + 
      prompt.slice(cursorPosition);
    
    setPrompt(newPrompt);
    setShowMentions(false);
    textareaRef.current.focus();
  };

  const handleMediaSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;

    try {
      setIsUploading(true);
      setUploadProgress(0);

      const file = files[0];
      if (!validateFileSize(file)) {
        toast({
          title: "Error",
          description: `File size must be ${MAX_FILE_SIZE}MB or less`,
          variant: "destructive",
        });
        return;
      }

      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 100);

      if (file.type.startsWith('image/')) {
        const compressedImage = await compressImage(file);
        setMediaFiles(prev => [...prev, compressedImage]);
        setMediaPreview(URL.createObjectURL(compressedImage));
      } else if (file.type.startsWith('video/')) {
        await validateVideo(file);
        setMediaFiles(prev => [...prev, file]);
        setMediaPreview(URL.createObjectURL(file));
      }

      clearInterval(progressInterval);
      setUploadProgress(100);
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
      }, 500);

    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      clearMedia();
    } finally {
      setIsUploading(false);
    }
  };

  const handleAttachmentSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files?.length) return;

    try {
      const file = files[0];
      
      if (!validateFileSize(file)) {
        toast({
          title: "Error",
          description: `File size must be ${MAX_FILE_SIZE}MB or less`,
          variant: "destructive",
        });
        return;
      }

      setIsUploading(true);
      setUploadProgress(0);

      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 100);

      setAttachmentFiles(prev => [...prev, file]);

      clearInterval(progressInterval);
      setUploadProgress(100);
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
      }, 500);

    } catch (error) {
      console.error('Error handling attachment:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to attach file",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      if (attachmentInputRef.current) {
        attachmentInputRef.current.value = '';
      }
    }
  };

  const clearMedia = () => {
    setMediaFiles([]);
    setMediaPreview(null);
    if (mediaInputRef.current) {
      mediaInputRef.current.value = '';
    }
  };

  const clearAttachment = () => {
    setAttachmentFiles([]);
    if (attachmentInputRef.current) {
      attachmentInputRef.current.value = '';
    }
  };

  const removeFile = (index: number, isMedia: boolean) => {
    if (isMedia) {
      setMediaFiles(prev => prev.filter((_, i) => i !== index));
      if (mediaFiles.length === 1) {
        setMediaPreview(null);
      }
    } else {
      setAttachmentFiles(prev => prev.filter((_, i) => i !== index));
    }
  };

  const createPost = useMutation({
    mutationFn: async (content: string) => {
      if (!user) throw new Error("Must be logged in to post");
      
      let mediaUrl = null;
      let mediaType = null;
      let attachmentUrl = null;
      let attachmentType = null;
      let attachmentName = null;

      setIsUploading(true);
      setUploadProgress(0);

      try {
        if (mediaFiles.length > 0) {
          const fileExt = mediaFiles[0].name.split('.').pop();
          const filePath = `${crypto.randomUUID()}.${fileExt}`;
          
          const { error: uploadError } = await supabase.storage
            .from('post-media')
            .upload(filePath, mediaFiles[0]);

          if (uploadError) throw uploadError;

          const { data } = supabase.storage
            .from('post-media')
            .getPublicUrl(filePath);

          mediaUrl = data.publicUrl;
          mediaType = mediaFiles[0].type;
        }

        if (attachmentFiles.length > 0) {
          const fileExt = attachmentFiles[0].name.split('.').pop();
          const filePath = `${crypto.randomUUID()}.${fileExt}`;
          
          const { error: uploadError } = await supabase.storage
            .from('post-attachments')
            .upload(filePath, attachmentFiles[0]);

          if (uploadError) throw uploadError;

          const { data } = supabase.storage
            .from('post-attachments')
            .getPublicUrl(filePath);

          attachmentUrl = data.publicUrl;
          attachmentType = attachmentFiles[0].type;
          attachmentName = attachmentFiles[0].name;
        }

        const { error } = await supabase
          .from("posts")
          .insert([{
            content: prompt,
            user_id: user?.id,
            media_url: mediaUrl,
            media_type: mediaType,
            attachment_url: attachmentUrl,
            attachment_type: attachmentType,
            attachment_name: attachmentName,
            is_private: isPrivate
          }]);

        if (error) throw error;

        queryClient.invalidateQueries({ queryKey: ["feed-posts"] });
        setPrompt("");
        setIsPrivate(false);
        clearMedia();
        clearAttachment();
        
        toast({
          title: "Success",
          description: "Your post has been published!",
        });

        return { success: true };
      } catch (error) {
        console.error('Error creating post:', error);
        toast({
          title: "Error",
          description: "Failed to create post. Please try again.",
          variant: "destructive",
        });
        throw error;
      } finally {
        setIsUploading(false);
      }
    }
  });

  const { data: mentionUsers = [] } = useQuery({
    queryKey: ["mention-users", mentionSearch],
    queryFn: async () => {
      if (!mentionSearch) return [];
      
      const { data, error } = await supabase
        .from("profiles")
        .select("id, username, avatar_url")
        .ilike("username", `${mentionSearch}%`)
        .limit(5);

      if (error) throw error;
      return data || [];
    },
    enabled: showMentions && mentionSearch.length > 0,
  });

  if (isProfileLoading) {
    return (
      <div className="p-4 bg-black/20">
        <div className="animate-pulse">
          <div className="h-20 bg-gray-700/20 rounded" />
          <div className="mt-4 h-8 bg-gray-700/20 rounded" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <div className="p-4 bg-black/20">
      <div className="space-y-4">
        <PostInput
          prompt={prompt}
          setPrompt={setPrompt}
          showMentions={showMentions}
          mentionUsers={mentionUsers}
          handleInput={handleInput}
          handleSelectMention={handleSelectMention}
          textareaRef={textareaRef}
          maxLength={maxLength}
        />
        
        <MediaPreview
          mediaPreview={mediaPreview}
          mediaFiles={mediaFiles}
          clearMedia={clearMedia}
        />

        <FileList
          files={mediaFiles}
          isMedia={true}
          removeFile={removeFile}
        />

        <FileList
          files={attachmentFiles}
          isMedia={false}
          removeFile={removeFile}
        />

        {isUploading && (
          <Progress 
            value={uploadProgress} 
            className="h-1 bg-white/10"
          />
        )}

        <input
          type="file"
          accept="image/*,video/*"
          onChange={handleMediaSelect}
          className="hidden"
          ref={mediaInputRef}
        />
        <input
          type="file"
          accept="*/*"
          onChange={handleAttachmentSelect}
          className="hidden"
          ref={attachmentInputRef}
        />

        <PostActions
          prompt={prompt}
          maxLength={maxLength}
          mediaFiles={mediaFiles}
          attachmentFiles={attachmentFiles}
          isPrivate={isPrivate}
          setIsPrivate={setIsPrivate}
          profile={profile}
          onMediaClick={() => mediaInputRef.current?.click()}
          onAttachmentClick={() => attachmentInputRef.current?.click()}
          onSubmit={() => createPost.mutate(prompt)}
        />
      </div>
    </div>
  );
};
